import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { apiFetch, getToken, API_BASE } from '../../services/apiClient';
import PdfWindow from '../../components/PdfWindow';
import ReceivablesBoard from './ReceivablesBoard';
import { DaysPills, BillHistoryWindow, billStage } from '../../components/BillBits';
import { offerClosePart, partLabel } from '../../utils/billParts';

// A work-order card's dates, as the shared day counters read them (Pat, Oct 7 2026).
export const woBillFacts = w => ({ finished_at: w.finished_at, created_at: w.created_at, prepared_at: w.wo_bill_prepared_at, sent_at: w.wo_bill_sent_at,
  paid_at: w.wo_paid_at, last_followup_at: w.last_followup_at, last_followup_kind: w.last_followup_kind, followup_count: w.followup_count });

// ─────────────────────────────────────────────────────────────────────────────
// Work Orders To Be Billed.
//
// A finished work order is money earned and not yet asked for, so it doesn't just
// drop off the field board — it lands here. One person prepares the bill, SOMEONE
// ELSE checks it and sends it, and only then does it move on. Two names, never the
// same one: one person doing both halves is the same as nobody checking. An
// Executive is the exception, so a stuck one can always be unstuck.
//
// Paid closes it. A closed work order comes off every board and files under the
// year it closed — still readable, just no longer live work.
// ─────────────────────────────────────────────────────────────────────────────

const COLUMNS = [
  { key: 'complete_to_bill', label: 'To Bill',           tint: '#15803d', hint: 'Finished. Needs a bill preparing.' },
  { key: 'ready_to_send',    label: 'Needs Checking',    tint: '#c2410c', hint: 'Prepared. Waiting on a second person.' },
  { key: 'billing_not_paid', label: 'Billed · Not Paid', tint: '#b45309', hint: 'Gone out. Waiting on the money.' },
  { key: 'paid',             label: 'Paid',              tint: '#166534', hint: 'Paid. Ready to close.' },
];

const money = n => (n == null || n === '' ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const shortDate = s => (s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
const card = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10 };
const btn = { padding: '7px 12px', borderRadius: 8, border: 'none', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' };
const inp = { border: '1px solid #d1d5db', borderRadius: 7, padding: '6px 8px', fontSize: 12.5, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' };

function me() {
  try { return JSON.parse(localStorage.getItem('titan_session') || 'null') || {}; } catch { return {}; }
}

export default function WoBilling() {
  // Followed here from the notification: ?wo=<id> is the bill someone wants checked,
  // so it is scrolled to and marked rather than left for you to find in four columns.
  const [searchParams] = useSearchParams();
  const wantWo = searchParams.get('wo');
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [years, setYears] = useState([]);
  const [openYear, setOpenYear] = useState(null);
  const [closed, setClosed] = useState([]);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [showReceivables, setShowReceivables] = useState(false);   // Billed · Not Paid, four columns wide by age
  const [q, setQ] = useState('');
  const [history, setHistory] = useState(null);   // the card opened big with its history (Pat, Oct 7 2026)
  const [viewing, setViewing] = useState(null);   // { title, url | fetchPath } — a PDF in a window over the board (Ryan, Oct 1 2026)   // search: invoice number, work order, job, customer, amount, names (Ryan, Sep 30 2026)
  const session = me();
  const isExec = ['Executive', 'admin', 'Admin'].includes(session.role);

  const load = useCallback(() => {
    apiFetch('/work-orders/billing').then(r => setRows(Array.isArray(r) ? r : [])).catch(e => setErr(e?.error || 'Could not load these.'));
    apiFetch('/work-orders/closed-years').then(y => setYears(Array.isArray(y) ? y : [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!openYear) { setClosed([]); return; }
    apiFetch(`/work-orders/billing?year=${openYear}`).then(r => setClosed(Array.isArray(r) ? r : [])).catch(() => setClosed([]));
  }, [openYear]);

  // A work-order card acts on the work order; an invoice card (a second or third bill on
  // the same work order) acts on that invoice (Ryan, Sep 30 2026).
  async function act(wo, path, body) {
    setBusy(wo.key || wo.id); setErr('');
    try {
      if (wo.card_kind === 'invoice') await apiFetch(`/invoices/${wo.invoice_id}${path}`, { method: 'POST', body: JSON.stringify(body || {}) });
      else await apiFetch(`/work-orders/${wo.id}${path}`, { method: 'POST', body: body ? JSON.stringify(body) : undefined });
      await load();
      // A bill just went out — close the T&M work it covered as a part? (WO26117, Oct 9 2026)
      if (path === '/send' && wo.card_kind === 'invoice') { if (await offerClosePart(wo.invoice_id)) await load(); }
      else if (path === '/bill/send' && wo.wo_bill_invoice_no) { if (await offerClosePart(null, { projectId: wo.id, invoiceNo: wo.wo_bill_invoice_no })) await load(); }
    } catch (e) { setErr(e?.error || 'That did not go through.'); }
    setBusy('');
  }

  // Every word typed has to be found somewhere on the card — number, name, customer,
  // invoice number, amount (with or without the comma), who prepared or sent it.
  const matches = useCallback(r => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return true;
    const amt = r.wo_bill_amount != null ? Number(r.wo_bill_amount) : null;
    const hay = [r.project_number, r.name, r.client_name, r.wo_bill_invoice_no, r.wo_bill_prepared_by_name, r.wo_bill_sent_by_name, r.wo_closed_by_name,
      amt != null ? String(amt) : '', amt != null ? amt.toFixed(2) : '', amt != null ? money(amt) : ''].filter(Boolean).join(' ').toLowerCase();
    return words.every(w => hay.includes(w));
  }, [q]);
  const byCol = useMemo(() => {
    const out = Object.fromEntries(COLUMNS.map(c => [c.key, []]));
    for (const r of rows || []) if (matches(r)) (out[r.wo_status] || out.complete_to_bill).push(r);
    return out;
  }, [rows, matches]);
  const shown = Object.values(byCol).reduce((n, l) => n + l.length, 0);
  const closedShown = useMemo(() => closed.filter(matches), [closed, matches]);

  if (err && !rows) return <AdminLayout><div style={{ padding: 30, color: '#b91c1c' }}>{err}</div></AdminLayout>;
  if (!rows) return <AdminLayout><div style={{ padding: 30, color: '#6b7280' }}>Loading…</div></AdminLayout>;

  const waiting = byCol.ready_to_send.filter(w => w.wo_bill_prepared_by !== session.userId || isExec).length;

  return (
    <AdminLayout>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
        <h1 style={{ fontSize: 21, fontWeight: 800, margin: 0 }}>Work Orders To Be Billed</h1>
        <span style={{ fontSize: 12.5, color: '#9ca3af', flex: 1 }}>
          Finished work orders, through to closed.
        </span>
        {waiting > 0 && (
          <span style={{ fontSize: 12, fontWeight: 800, color: '#c2410c', background: '#fff7ed',
            border: '1px solid #fed7aa', borderRadius: 999, padding: '4px 12px' }}>
            {waiting} waiting on you to check
          </span>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ fontSize: 12.5, color: '#6b7280', flex: 1, minWidth: 240 }}>
          Whoever prepares a bill can’t be the one who sends it — someone else checks it first.
        </div>
        <div style={{ position: 'relative', width: 'min(100%, 340px)' }}>
          <input type="search" value={q} onChange={e => setQ(e.target.value)} aria-label="Search invoices"
            placeholder="Search — invoice #, WO number, job, customer, amount…"
            style={{ ...inp, padding: '8px 30px 8px 12px', borderRadius: 999, fontSize: 13 }} />
          {q && <button onClick={() => setQ('')} aria-label="Clear search" style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none', background: '#f3f4f6', borderRadius: 999, width: 22, height: 22, fontSize: 12, cursor: 'pointer', color: '#374151' }}>✕</button>}
        </div>
        {q.trim() && <span style={{ fontSize: 12, fontWeight: 700, color: shown ? '#374151' : '#b91c1c', whiteSpace: 'nowrap' }}>{shown} on the board{closed.length ? ` · ${closedShown.length} in ${openYear}` : ''}{!shown && !years.length ? '' : (!shown && !openYear ? ' — closed years are searched once you open one below' : '')}</span>}
      </div>

      {err && <div style={{ fontSize: 12.5, color: '#b91c1c', fontWeight: 700, marginBottom: 10 }}>{err}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(230px, 1fr))', gap: 12, alignItems: 'start', overflowX: 'auto' }}>
        {COLUMNS.map(c => (
          <div key={c.key} style={{ background: 'var(--surface-2, #f7f7f5)', borderRadius: 12, padding: 9, minHeight: 150 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '3px 4px' }}>
              <span style={{ width: 8, height: 8, borderRadius: 4, background: c.tint }} />
              <span style={{ fontSize: 12.5, fontWeight: 800, color: '#111' }}>{c.label}</span>
              {/* Sits in the header row beside the label, small, so the four columns stay level (Pat, Sep 2026). */}
              {c.key === 'billing_not_paid' && (
                <button onClick={() => setShowReceivables(true)} title="Open this column four wide: 30 / 60 / 90 / 120 days, with follow-ups"
                  style={{ ...btn, padding: '2px 8px', fontSize: 11, background: '#b45309', color: '#fff', whiteSpace: 'nowrap' }}>
                  📊 Breakdown
                </button>
              )}
              <span style={{ fontSize: 11.5, color: '#9ca3af', marginLeft: 'auto' }}>{byCol[c.key].length}</span>
            </div>
            <div style={{ fontSize: 11, color: '#9ca3af', padding: '0 4px 9px' }}>{c.hint}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {byCol[c.key].map(w => (
                <WoCard key={w.key || w.id} wo={w} column={c.key} busy={busy === (w.key || w.id)} session={session} isExec={isExec}
                  highlight={wantWo === w.id}
                  onOpen={() => setHistory({ w, column: c.key })}
                  onAct={(path, body) => act(w, path, body)}
                  onChanged={load} onError={setErr} onView={setViewing} />
              ))}
              {byCol[c.key].length === 0 && <div style={{ fontSize: 11.5, color: '#c4c4c4', padding: '8px 6px' }}>Nothing here.</div>}
            </div>
          </div>
        ))}
      </div>

      {history && (() => {
        const w = history.w, isInv = w.card_kind === 'invoice';
        return <BillHistoryWindow projectId={isInv ? null : w.id} invoiceId={isInv ? w.invoice_id : null}
          title={`${w.project_number}${w.wo_bill_invoice_no ? ` · Invoice ${w.wo_bill_invoice_no}` : ''}`}
          sub={[w.name, w.client_name, w.wo_bill_amount != null ? money(w.wo_bill_amount) : null, COLUMNS.find(c => c.key === history.column)?.label].filter(Boolean).join(' · ')}
          stage={billStage(history.column)} facts={woBillFacts(w)}
          openHref={isInv ? `/admin/jobs/${w.id}?pillow=wo_billing` : `/admin/jobs/${w.id}/job_info`} openLabel="Open the work order ›"
          onNavigate={navigate} onClose={() => setHistory(null)} />;
      })()}
      {viewing && <PdfWindow title={viewing.title} url={viewing.url || null} fetchPath={viewing.fetchPath || null} onClose={() => setViewing(null)} />}
      {showReceivables && <ReceivablesBoard onClose={() => { setShowReceivables(false); load(); }} />}

      {/* Closed, by the year it closed. Off every board, still readable. */}
      <div style={{ marginTop: 26 }}>
        <h2 style={{ fontSize: 15, fontWeight: 800, margin: '0 0 8px' }}>Closed</h2>
        {years.length === 0 ? (
          <div style={{ fontSize: 13, color: '#9ca3af' }}>Nothing closed yet.</div>
        ) : (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {years.map(y => (
              <button key={y.year} onClick={() => setOpenYear(openYear === y.year ? null : y.year)}
                style={{ ...btn, background: openYear === y.year ? '#1e3a5f' : '#fff', color: openYear === y.year ? '#fff' : '#374151',
                  border: '1px solid #e5e7eb' }}>
                {y.year} <span style={{ opacity: 0.7 }}>({y.n})</span>
              </button>
            ))}
          </div>
        )}
        {openYear && (
          <div style={{ ...card, marginTop: 10, padding: 10 }}>
            {closed.length === 0 ? <div style={{ fontSize: 13, color: '#9ca3af' }}>Loading…</div> : closedShown.length === 0 ? <div style={{ fontSize: 13, color: '#9ca3af' }}>Nothing in {openYear} matches “{q.trim()}”.</div> : closedShown.map(w => (
              <div key={w.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 4px', borderBottom: '1px solid #f3f4f6', flexWrap: 'wrap' }}>
                <button onClick={() => navigate(`/admin/jobs/${w.id}/job_info`)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', textAlign: 'left' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: '#c2410c' }}>{w.project_number}</span>
                  <span style={{ fontSize: 12, color: '#6b7280' }}> · {w.name}</span>
                </button>
                <span style={{ fontSize: 12, fontWeight: 700, marginLeft: 'auto' }}>{money(w.wo_bill_amount)}</span>
                <span style={{ fontSize: 11.5, color: '#9ca3af' }}>closed {shortDate(w.wo_closed_at)}</span>
                <button onClick={() => act(w, '/reopen')} style={{ ...btn, background: '#fff', border: '1px solid #e5e7eb', color: '#6b7280', fontSize: 11.5 }}>
                  Reopen
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function WoCard({ wo, column, busy, session, isExec, onOpen, onAct, onChanged, onError, highlight, onView }) {
  const [amount, setAmount] = useState(wo.wo_bill_amount ?? '');
  const ref = React.useRef(null);
  useEffect(() => {
    if (highlight && ref.current) ref.current.scrollIntoView({ block: 'center' });
  }, [highlight]);
  const [invNo, setInvNo] = useState(wo.wo_bill_invoice_no || '');
  const mine = wo.wo_bill_prepared_by === session.userId;
  const canSend = !mine || isExec;
  const isInv = wo.card_kind === 'invoice';

  async function upload(file) {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await fetch(`${API_BASE}/work-orders/${wo.id}/bill/files`, {
        method: 'POST', headers: { 'x-worker-token': getToken() }, body: fd,
      });
      if (!r.ok) throw new Error();
      await onChanged();
    } catch { onError('That file did not upload.'); }
  }

  return (
    <div ref={ref} style={{ ...card, padding: '9px 10px', opacity: busy ? 0.5 : 1,
      ...(highlight ? { border: '2px solid #e87722', boxShadow: '0 0 0 4px #fff3e6' } : null) }}>
      <button onClick={onOpen} style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', textAlign: 'left', cursor: 'pointer', width: '100%' }}>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: '#c2410c' }}>{wo.project_number}</div>
        <div style={{ fontSize: 11.5, color: '#4b5563', lineHeight: 1.35 }}>{wo.name}</div>
        <DaysPills stage={billStage(column)} facts={woBillFacts(wo)} />
      </button>
      {isInv && (
        <div style={{ marginTop: 4, display: 'inline-block', fontSize: 10.5, fontWeight: 800, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 999, padding: '1px 8px' }}>
          Invoice {wo.wo_bill_invoice_no}{wo.bill_part_name ? ` · ${partLabel(wo.bill_part_name)}` : ''}{wo.bill_count > 1 ? ` · bill ${wo.bill_seq} of ${wo.bill_count} on this work order` : ''}
        </div>
      )}

      {wo.wo_bill_amount != null && (
        <div style={{ fontSize: 13.5, fontWeight: 800, color: '#111', marginTop: 5 }}>
          {money(wo.wo_bill_amount)}
          {wo.wo_bill_invoice_no && <span style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af' }}> · {wo.wo_bill_invoice_no}</span>}
        </div>
      )}

      {/* Both names, so it is obvious who has looked at it and who hasn't. */}
      {wo.wo_bill_prepared_by_name && (
        <div style={{ fontSize: 10.5, color: '#6b7280', marginTop: 4 }}>
          Prepared by {wo.wo_bill_prepared_by_name} {shortDate(wo.wo_bill_prepared_at)}
          {wo.wo_bill_sent_by_name && <><br />Checked &amp; sent by {wo.wo_bill_sent_by_name} {shortDate(wo.wo_bill_sent_at)}</>}
        </div>
      )}

      {/* Once a bill has been prepared there is always something to say here: the
          invoice, or the fact that nobody attached one. */}
      {isInv && (
        <button onClick={() => onView({ title: `${wo.project_number} · Invoice ${wo.wo_bill_invoice_no || ''}`.trim(), fetchPath: `/invoices/${wo.invoice_id}/pdf` })}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', marginTop: 7, padding: '7px 10px', borderRadius: 8, border: '1.5px solid #1d4ed8', background: '#eff6ff', color: '#1d4ed8', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
          🧾 View invoice
        </button>
      )}
      {!isInv && wo.wo_bill_amount != null && (
        (wo.bill_files || []).length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 7 }}>
            {wo.bill_files.map(bf => (
              <button key={bf.id} type="button" onClick={() => onView({ title: bf.original_name || `${wo.project_number} invoice`, url: `${API_BASE.replace('/api', '')}/uploads/${bf.filename}` })}
                title={bf.original_name}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%',
                  padding: '7px 10px', borderRadius: 8, border: '1.5px solid #1d4ed8',
                  background: '#eff6ff', color: '#1d4ed8', fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
                🧾 View invoice{(wo.bill_files || []).length > 1 && bf.original_name ? ` · ${bf.original_name.replace(/.pdf$/i, '').replace(wo.project_number, '').trim()}` : ''}
              </button>
            ))}
          </div>
        ) : (
          <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            marginTop: 7, padding: '7px 10px', borderRadius: 8, cursor: 'pointer',
            border: '1.5px dashed #fbbf24', background: '#fffbeb', color: '#92400e',
            fontSize: 11.5, fontWeight: 800 }}>
            ⚠ No invoice attached — add one
            <input type="file" style={{ display: 'none' }}
              onChange={e => { upload(e.target.files?.[0]); e.target.value = ''; }} />
          </label>
        )
      )}

      {column === 'complete_to_bill' && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div style={{ display: 'flex', gap: 5 }}>
            <input style={inp} type="number" step="any" placeholder="Amount" value={amount}
              onChange={e => setAmount(e.target.value)} />
            <input style={inp} placeholder="Invoice #" value={invNo} onChange={e => setInvNo(e.target.value)} />
          </div>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#c2410c', cursor: 'pointer' }}>
            ＋ Attach the invoice
            <input type="file" style={{ display: 'none' }} onChange={e => { upload(e.target.files[0]); e.target.value = ''; }} />
          </label>
          <button disabled={amount === ''} onClick={() => onAct('/bill', { wo_bill_amount: amount, wo_bill_invoice_no: invNo })}
            style={{ ...btn, background: amount === '' ? '#e5e7eb' : '#c2410c', color: amount === '' ? '#9ca3af' : '#fff' }}>
            Prepare the bill
          </button>
        </div>
      )}

      {column === 'ready_to_send' && (
        <div style={{ marginTop: 8 }}>
          <button disabled={!canSend} onClick={() => onAct(isInv ? '/send' : '/bill/send')}
            title={canSend ? 'Check it and send it' : 'You prepared this one — it needs someone else'}
            style={{ ...btn, width: '100%', background: canSend ? '#065f46' : '#f3f4f6', color: canSend ? '#fff' : '#9ca3af' }}>
            {canSend ? '✓ Checked — send it' : 'Waiting on someone else'}
          </button>
        </div>
      )}

      {column === 'billing_not_paid' && (
        <button onClick={() => { if (!isInv || window.confirm(`Mark invoice ${wo.wo_bill_invoice_no} on ${wo.project_number} paid?`)) onAct('/paid'); }} style={{ ...btn, width: '100%', marginTop: 8, background: '#166534', color: '#fff' }}>
          Mark paid
        </button>
      )}

      {column === 'paid' && (
        <button onClick={() => onAct('/close')} style={{ ...btn, width: '100%', marginTop: 8, background: '#1e3a5f', color: '#fff' }}>
          Close it
        </button>
      )}

      {/* The way back: a bill prepared wrong (a $0 print, the wrong amount) goes back to
          To Bill to be done again. Quiet on purpose — it is the exception, not a step. */}
      {isInv && column === 'ready_to_send' && (
        <button onClick={() => { if (window.confirm(`Put invoice ${wo.wo_bill_invoice_no} back to draft on ${wo.project_number}? The number stays with it.`)) onAct('/reopen'); }}
          style={{ background: 'none', border: 'none', padding: '6px 0 0', width: '100%', cursor: 'pointer', fontSize: 11, fontWeight: 700, color: '#6b7280', textAlign: 'center' }}>
          ↩ Back to draft
        </button>
      )}
      {!isInv && column !== 'complete_to_bill' && (
        <button
          onClick={() => { if (window.confirm(`Send ${wo.project_number} back to To Bill? Who prepared, sent and paid it is cleared so the bill can be done again. The invoice number and any attached file stay.`)) onAct('/bill/undo'); }}
          style={{ background: 'none', border: 'none', padding: '6px 0 0', width: '100%', cursor: 'pointer', fontSize: 11, fontWeight: 700, color: '#6b7280', textAlign: 'center' }}>
          ↩ Back to To Bill
        </button>
      )}
    </div>
  );
}
